import type { MailLinkForContactDto } from "../../services/mailLinkService";
import type { CallLogDto } from "../../services/telephonyService";
import type { DocumentDto } from "../../services/documentService";
import { DIRECTION_LABEL, displayNameOf } from "../telephony/telephonyUtils";
import { alsWeitererKontakt } from "../telephony/callKontakte";

/**
 * Mails, Anrufe und Dokumente als Verlaufseinträge des Kundentagebuchs
 * (PO 08.09.2026). Reine Ableitung — der Verlauf soll dieselben drei Arten
 * zeigen wie der Vorgangs-Verlauf (V3), nur ohne dessen vorgangsgebundene
 * Aktionen. Richtung und Anzeigename der Anrufe kommen deshalb aus denselben
 * Helfern wie dort (`telephonyUtils`), damit derselbe Anruf nicht an zwei
 * Stellen unterschiedlich heißt.
 */

export type KommEintrag = {
    id: string;
    ts: Date;
    kind: "mail" | "anruf" | "dokument";
    title: string;
    /** Einzeiler unter dem Titel, solange die Zeile zugeklappt ist. */
    summary?: string;
    /** Wer — steht im aufgeklappten Bereich. */
    actor?: string;
    /** Freitext für den aufgeklappten Bereich (Textanriss bzw. Notiz). */
    text?: string;
};

const zeit = (iso?: string | null): Date => {
    if (!iso) return new Date(0);
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

/** Dauer wie im Vorgangs-Verlauf: „4:12 Min." bzw. „45 Sek.". */
const dauer = (sek?: number | null): string | undefined => {
    if (sek == null || !Number.isFinite(sek) || sek <= 0) return undefined;
    const m = Math.floor(sek / 60);
    const s = Math.floor(sek % 60);
    return m ? `${m}:${String(s).padStart(2, "0")} Min.` : `${s} Sek.`;
};

const text = (v?: string | null): string | undefined => v?.trim() || undefined;

export function mailEintraege(links: MailLinkForContactDto[]): KommEintrag[] {
    return links.map((l) => ({
        id: `mail-${l.id}`,
        // `receivedAt` ist der fachliche Zeitpunkt; fehlt er (Alt-Links), tritt
        // der Verknüpfungszeitpunkt ein — sonst stünde der Eintrag bei 1970.
        ts: zeit(l.receivedAt ?? l.linkedAt),
        kind: "mail" as const,
        title: text(l.subject) ?? "(Kein Betreff)",
        ...(text(l.preview) ? { summary: text(l.preview) } : {}),
        ...(text(l.senderName) ?? text(l.senderEmail)
            ? { actor: text(l.senderName) ?? text(l.senderEmail) }
            : {}),
        ...(text(l.preview) ? { text: text(l.preview) } : {}),
    }));
}

/**
 * `kontaktId`: der Kontakt, dessen Verlauf gezeigt wird. Hängt er nur als
 * WEITERER Kontakt am Anruf, trägt der Titel den Namen des Hauptkontakts —
 * der Zusatz „(als weiterer Kontakt)" erklärt, warum der Anruf hier steht.
 */
export function anrufEintraege(calls: CallLogDto[], kontaktId?: number): KommEintrag[] {
    return calls.map((c) => {
        const d = dauer(c.talkDurationSec ?? c.callDurationSec);
        const weiterer = alsWeitererKontakt(c, kontaktId) ? " (als weiterer Kontakt)" : "";
        return {
            id: `anruf-${c.id}`,
            ts: zeit(c.startedAt),
            kind: "anruf" as const,
            title: `${displayNameOf(c)}${weiterer} · ${DIRECTION_LABEL[c.direction] ?? "Anruf"}`,
            ...(d ? { summary: d } : {}),
            // „Angenommen von" — im Tagebuch die einzige Spur, wer telefoniert hat.
            ...(text(c.userName) ? { actor: text(c.userName) } : {}),
            ...(text(c.notes) ? { text: text(c.notes) } : {}),
        };
    });
}

export function dokumentEintraege(docs: DocumentDto[]): KommEintrag[] {
    return docs.map((d) => ({
        id: `dokument-${d.id}`,
        ts: zeit(d.uploadedAt),
        kind: "dokument" as const,
        title: text(d.fileName) ?? "Dokument",
        ...(text(d.category) ? { summary: text(d.category) } : {}),
    }));
}
